import { useEffect, useMemo, useState, Component } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, Save, User, Users, Briefcase, Flag, Wand2, Copy, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";

const CANDIDATE_IMPORT_PROMPT = `I've been using you to help with my political campaign and I'm moving to a new campaign platform. Please summarize everything you know about my campaign and race into a clearly organized briefing using these exact headings. If you don't have information for a heading, write "Not provided." Be factual, specific, and concise.

CANDIDATE OR COMMITTEE NAME:

OFFICE SOUGHT:

DISTRICT / JURISDICTION:

ELECTION DATE:

PRIMARY DATE (if any):

PARTY:

RACE STATUS: (Am I the incumbent, a challenger, or is this an open seat?)

CANDIDATE EXPERIENCE: (First-time candidate or have I run / held office before?)

PARTISAN LEAN OF THE SEAT: (Safe Republican, Lean Republican, Tossup, Lean Democrat, Safe Democrat, and any basis for this)

RECENT ELECTION RESULTS: (How has this district voted in recent past elections?)

OPPONENT(S): (Names, and whether they are an incumbent or challenger)

NUMBER OF CANDIDATES IN THE RACE:

ENDORSEMENTS: (Known endorsements for me and for opponents)

BUDGET AND FUNDRAISING: (Roughly how much have I raised or budgeted?)

CAMPAIGN ORGANIZATION: (Paid staff or volunteers? Do I have a treasurer, manager, consultant?)

WHAT'S ALREADY BUILT: (Website, social media, signs, materials, etc.)

DISTRICT PROFILE: (Geography — rural, suburban, urban; key demographics; dominant local issues; any notable local controversies)

MY TOP ISSUES:

MY CORE MESSAGE:

MY STRENGTHS:

MY WEAKNESSES AND LIKELY ATTACKS AGAINST ME:

MY BACKGROUND STORY:

OPPONENT'S PLATFORM AND POSITIONS:

OPPONENT'S VULNERABILITIES:

MY PREFERRED COMMUNICATION STYLE AND TONE:

HOW AGGRESSIVE I WANT MY MESSAGING TO BE:

TIME I CAN COMMIT TO THE CAMPAIGN:

WHAT I'M GOOD AT VERSUS WHERE I NEED THE MOST HELP:

ANY OTHER IMPORTANT CONTEXT:

Output only this briefing with these headings, nothing else.`;

const COMMITTEE_IMPORT_PROMPT = `I lead a local political party committee and I'm moving our work to a new platform built for campaigns and committees. Please summarize everything you know about our committee and our county's political landscape into a clearly organized briefing using these exact headings. If you don't have information for a heading, write "Not provided." Be factual, specific, and concise.

COMMITTEE NAME:

COUNTY / JURISDICTION:

PARTY:

MY ROLE: (Chair, Vice Chair, Executive Director, etc.)

COMMITTEE OVERVIEW:

- Size and structure of the committee (number of members, districts/towns covered, key officers)
- How active and organized the committee currently is

THE COUNTY'S POLITICAL LANDSCAPE:

- Overall partisan lean of the county (Safe R, Lean R, Tossup, Lean D, Safe D) and basis for it
- Recent election results and trends across the county
- Voter registration breakdown if known
- Key towns or districts that are strongholds, swing areas, or weak spots for our party

OUR SLATE AND CANDIDATES:

- Offices on the ballot this cycle that we're supporting candidates for
- Names of our candidates and the races they're in
- Which races are competitive, safe, or long-shots
- Any candidate recruitment gaps (offices we still need candidates for)

THE OPPOSITION:

- The opposing party committee and its strength/activity level
- Their key candidates and where they're strongest
- Their likely messaging and strategy this cycle

OUR PRIORITIES AND GOALS:

- Our top goals this cycle (seats to win, seats to defend, turnout targets)
- Our top county-wide issues and messaging themes
- Key contrasts we want to draw with the opposition

COMMITTEE OPERATIONS:

- Fundraising: roughly where we stand, key events (Lincoln Day Dinner, etc.)
- Volunteer and field strength (canvassing, phone banking, poll watching capacity)
- Recurring committee events and traditions
- What's already built (committee website, social media presence, email list, donor list)

OUR VOICE AND STYLE:

- The committee's preferred communication tone (institutional, grassroots, aggressive, measured)
- How we typically communicate with members, voters, and press
- What we're strong at versus where we most need help

KEY DATES:

- Election and primary dates
- Major committee events and deadlines

ANY OTHER IMPORTANT CONTEXT:

Output only this briefing with these headings, nothing else.`;

const STAFFER_IMPORT_PROMPT = `I am a campaign staff member and I am moving my campaign work to a new platform. Please summarize everything you know about the campaign I work for and my role into a clearly organized briefing using these exact headings. If you do not have information for a heading, write "Not provided." Be factual and concise.

MY NAME:
MY STAFF ROLE:
CANDIDATE I WORK FOR:
PARTY:
OFFICE THE CANDIDATE IS SEEKING:
DISTRICT / JURISDICTION:
ELECTION DATE:
PRIMARY DATE (if any):

THE CANDIDATE: background, top issues, core message, preferred tone

THE OPPONENT: name, positions, vulnerabilities

STATE OF THE RACE: incumbent/challenger/open seat, partisan lean, how competitive

CAMPAIGN MESSAGE AND STRATEGY: our core message and contrasts we want to draw

THE OPERATION:
- Staff: size and key roles
- Volunteers: how many, how organized, capacity (canvassing, phone banks, etc.)
- Field plan: voter contact goals and current progress
- Communications: our channels, press relationships, social presence
- Fundraising: rough status and key events
- What is already built: website, social, lists, materials

MY RESPONSIBILITIES: what I am specifically responsible for

WHAT I MOST NEED HELP WITH:

KEY DATES AND DEADLINES:

ANY OTHER IMPORTANT CONTEXT:

Output only this briefing with these headings, nothing else.`;


const emptyShared = {
  account_type: "candidate",
  bio: "",
  top_issues: ["", "", ""],
  own_weaknesses: "",
  tone: "professional",
  // candidate
  candidate_name: "",
  office_sought: "",
  district: "",
  election_date: "",
  opponent_name: "",
  opponent_platform: "",
  opponent_vulnerabilities: "",
  // committee
  committee_name: "",
  jurisdiction: "",
  party: "",
  key_events: "",
  opposition_messaging: "",
  // staffer
  staffer_name: "",
  staffer_role: "",
  staffer_responsibilities: "",
  staffer_help_needed: "",
  // State of the Race
  primary_date: "",
  race_status: "",
  candidate_experience: "",
  partisan_lean: "",
  recent_election_results: "",
  contested_primary: "",
  endorsements: "",
  budget_fundraising: "",
  organization: "",
  already_built: "",
  district_profile: "",
  core_message: "",
  strengths: "",
  background_story: "",
  communication_style: "",
  aggressiveness: "",
  time_commitment: "",
  strengths_vs_needs: "",
  other_context: "",
};

const TONES = [
  { value: "folksy", label: "Folksy — warm, neighborly, plainspoken" },
  { value: "professional", label: "Professional — measured, polished, civic" },
  { value: "fiery", label: "Fiery — bold, urgent, fighting" },
  { value: "measured", label: "Measured — steady, thoughtful, dignified" },
];

const CANDIDATE_STEPS = [
  { id: 1, title: "About you", desc: "The basics about you and the race." },
  { id: 2, title: "Your platform", desc: "What you stand for." },
  { id: 3, title: "The opponent", desc: "Know thy opponent." },
  { id: 4, title: "Your voice", desc: "How you sound on the trail." },
  { id: 5, title: "State of the Race", desc: "Optional — sharper context, sharper advice." },
];

const COMMITTEE_STEPS = [
  { id: 1, title: "Committee basics", desc: "Who you are and the area you cover." },
  { id: 2, title: "Platform & events", desc: "Your countywide priorities and what the committee runs." },
  { id: 3, title: "The opposition", desc: "What the other side is saying — and your vulnerabilities to avoid." },
  { id: 4, title: "Your voice", desc: "How the committee sounds publicly." },
  { id: 5, title: "State of the Race", desc: "Optional — strategic context for sharper guidance." },
];

const SOTR_FIELDS = [
  // group, key, label, helper, multiline
  ["Race fundamentals", "race_status", "Incumbent / challenger / open seat", "Are you the incumbent, a challenger, or is this an open seat?", false],
  ["Race fundamentals", "candidate_experience", "First-time or experienced", "First-time candidate, or have you run / held office before?", false],
  ["Race fundamentals", "partisan_lean", "Partisan lean of the seat", "Safe R, Lean R, Tossup, Lean D, Safe D — and any basis.", false],
  ["Race fundamentals", "recent_election_results", "Recent election results in the district", "How has this district voted in recent past elections?", true],
  ["Race fundamentals", "contested_primary", "Contested primary?", "Yes / no, plus details if relevant.", false],
  ["Race fundamentals", "primary_date", "Primary date", "If there's a primary — yyyy-mm-dd or any clear format.", false],
  ["The field", "endorsements", "Endorsements", "Known endorsements for you and for opponents.", true],
  ["Resources & organization", "budget_fundraising", "Budget / fundraising", "Roughly how much have you raised or budgeted?", true],
  ["Resources & organization", "organization", "Campaign organization", "Paid staff or volunteers? Do you have a treasurer, manager, consultant?", true],
  ["Resources & organization", "already_built", "What's already built", "Website, social media accounts, signs, palm cards, etc.", true],
  ["The district", "district_profile", "District profile", "Geography (rural/suburban/urban), key demographics, dominant local issues, notable controversies.", true],
  ["Candidate position", "core_message", "Your core message", "The one thing you most want voters to remember.", true],
  ["Candidate position", "strengths", "Your strengths", "Why you, why now — what you bring to this race.", true],
  ["Candidate position", "background_story", "Background story", "The story you'll tell at the door and on stage.", true],
  ["Working style", "communication_style", "Preferred communication style", "Beyond the tone above — anything specific about how you want to sound.", false],
  ["Working style", "aggressiveness", "How aggressive should the messaging be?", "Soft contrast / firm contrast / hard-edged — your call.", false],
  ["Working style", "time_commitment", "Time you can commit", "Hours per week, evenings/weekends only, full-time — whatever's real.", false],
  ["Working style", "strengths_vs_needs", "What you're good at vs. need most help with", "Where you're strong, where you most need Electable to do the heavy lifting.", true],
  ["Working style", "other_context", "Anything else worth knowing", "Catch-all for anything important the categories above missed.", true],
];

// Committee-flavored State of the Race — same field keys, county-level framing.
const SOTR_FIELDS_COMMITTEE = [
  ["Committee fundamentals", "candidate_experience", "Your role on the committee", "Chair, Vice Chair, Executive Director, etc.", false],
  ["Committee fundamentals", "background_story", "Committee overview", "Size and structure (members, districts/towns covered, key officers); how active and organized you currently are.", true],
  ["The county", "partisan_lean", "Overall partisan lean of the county", "Safe R, Lean R, Tossup, Lean D, Safe D — and the basis for it.", false],
  ["The county", "recent_election_results", "Recent countywide election results & trends", "How the county has voted in recent past elections (and any voter registration breakdown).", true],
  ["The county", "district_profile", "County political landscape", "Key towns or districts that are strongholds, swing areas, or weak spots; demographics; dominant local issues.", true],
  ["The county", "primary_date", "Primary date", "If there's a primary on the slate — yyyy-mm-dd or any clear format.", false],
  ["The slate", "other_context", "Our slate & candidates", "Offices on the ballot, candidate names, which races are competitive vs safe vs long-shots, recruitment gaps.", true],
  ["The slate", "endorsements", "Endorsements", "Notable endorsements for our slate (and for opposition).", true],
  ["The opposition", "opposition_messaging", "Opposing committee — strength & key candidates", "Their activity level, key candidates, where they're strongest. (You can also fill this on Step 3.)", true],
  ["Goals & messaging", "core_message", "Top goals & messaging themes", "Seats to win/defend, turnout targets, top countywide messaging themes, contrasts with the opposition.", true],
  ["Goals & messaging", "strengths", "Where the committee is strongest", "What we lead on. Why voters and donors should follow our slate.", true],
  ["Committee operations", "budget_fundraising", "Fundraising", "Roughly where we stand and key events (Lincoln Day Dinner, fall fundraiser, etc.).", true],
  ["Committee operations", "organization", "Volunteer & field strength", "Canvassing, phone banking, poll watching capacity, paid staff if any.", true],
  ["Committee operations", "already_built", "What's already built", "Committee website, social media, email list, donor list, signage, etc.", true],
  ["Voice & style", "communication_style", "Preferred committee tone", "Institutional, grassroots, aggressive, measured — and how we typically communicate with members, voters, press.", true],
  ["Voice & style", "aggressiveness", "How aggressive should the messaging be?", "Soft contrast / firm contrast / hard-edged — your call.", false],
  ["Voice & style", "strengths_vs_needs", "What we're good at vs. where we need help", "Where the committee is strong, where you most need Electable to do the heavy lifting.", true],
];

// Precomputed once at module load — the group order and per-group fields are
// static, so no per-render Set/filter work in the JSX below.
const groupSotrFields = (fields) => {
  const groups = [];
  const byGroup = new Map();
  for (const f of fields) {
    if (!byGroup.has(f[0])) {
      byGroup.set(f[0], []);
      groups.push(f[0]);
    }
    byGroup.get(f[0]).push(f);
  }
  return { groups, byGroup };
};
const SOTR_GROUPED = groupSotrFields(SOTR_FIELDS);
const SOTR_GROUPED_COMMITTEE = groupSotrFields(SOTR_FIELDS_COMMITTEE);

/**
 * Step-scoped error boundary. Wraps the intake step content so a crash in
 * ONE step (e.g. an undefined field read like the July 2026 `SOTR_FIELDS`
 * bug that blanked step 5) surfaces a readable retry banner instead of a
 * blank white page. Resets automatically when the parent step number
 * changes, so backing out and forward again gives a fresh attempt.
 */
class IntakeStepBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // Surface for triage. A blank white page in the signup funnel is the
    // worst possible failure mode — we want this in the console every time.
    console.error("[CampaignIntake] step crashed:", err, info?.componentStack);
  }
  componentDidUpdate(prev) {
    // Reset on step change so an old failure doesn't stick around.
    if (prev.stepKey !== this.props.stepKey && this.state.err) {
      this.setState({ err: null });
    }
  }
  render() {
    if (this.state.err) {
      return (
        <div className="rounded-lg border-2 border-flag-red bg-red-50 px-4 py-4 space-y-2" data-testid="intake-step-error">
          <div className="font-semibold text-flag-red">This step hit an unexpected error.</div>
          <div className="text-xs text-slate-600 break-all">{String(this.state.err?.message || this.state.err)}</div>
          <button
            type="button"
            onClick={() => this.setState({ err: null })}
            className="mt-1 inline-flex items-center gap-1 rounded-md bg-flag-navy px-3 py-1.5 text-xs font-bold text-white hover:bg-flag-navy-2"
            data-testid="intake-step-retry"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function CampaignIntake() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [pickedType, setPickedType] = useState(false); // shows type picker until true
  const [importMode, setImportMode] = useState(false); // shows import panel when true
  const [form, setForm] = useState(emptyShared);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Import state
  const [pasteText, setPasteText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    api
      .get("/campaign")
      .then(({ data }) => {
        if (data) {
          const issues = Array.isArray(data.top_issues) ? data.top_issues.slice(0, 3) : [];
          while (issues.length < 3) issues.push("");
          setForm({ ...emptyShared, ...data, top_issues: issues });
          setPickedType(true); // editing an existing profile — skip the picker
          return;
        }
        // No existing profile — honor ?type= from signup so we skip the duplicate picker
        const presetType = searchParams.get("type");
        if (presetType === "candidate" || presetType === "committee") {
          setForm((f) => ({ ...f, account_type: presetType }));
          setPickedType(true);
        }
      })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIssue = (i, v) =>
    setForm((f) => {
      const next = [...f.top_issues];
      next[i] = v;
      return { ...f, top_issues: next };
    });

  const isCommittee = form.account_type === "committee";
  const isStaffer = form.account_type === "staffer";
  const STAFFER_STEPS = [
    { id: 1, title: "About you & the candidate", desc: "Who you are, your role, and the candidate you work for." },
    { id: 2, title: "Platform & operation", desc: "What the campaign stands for and how the ops are set up." },
    { id: 3, title: "The opposition", desc: "Who the opponent is and what we contrast against." },
    { id: 4, title: "Campaign voice", desc: "How the candidate / campaign sounds." },
    { id: 5, title: "State of the Race", desc: "Optional — sharper context, sharper advice." },
  ];
  const STEPS = isStaffer ? STAFFER_STEPS : isCommittee ? COMMITTEE_STEPS : CANDIDATE_STEPS;

  const canNext = useMemo(() => {
    if (step === 1) {
      if (isStaffer) {
        return form.staffer_name.trim() && form.staffer_role.trim() && form.candidate_name.trim() && form.office_sought.trim();
      }
      return isCommittee
        ? form.committee_name.trim() && form.jurisdiction.trim() && form.party.trim()
        : form.candidate_name.trim() && form.office_sought.trim();
    }
    if (step === 2) return form.top_issues.filter((x) => x.trim()).length >= 1;
    if (step === 3) return true;
    if (step === 4) return !!form.tone;
    if (step === 5) return true;
    return true;
  }, [step, form, isCommittee, isStaffer]);

  const save = async (e) => {
    if (e) e.preventDefault();
    // Guard: if the user pressed Enter (form submit) before reaching the last step,
    // advance instead of saving prematurely.
    if (step < STEPS.length) {
      if (canNext) setStep((s) => s + 1);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        top_issues: form.top_issues.map((t) => t.trim()).filter(Boolean),
      };
      await api.put("/campaign", payload);
      navigate("/settings");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-slate-500">Loading…</div>;
  }

  // ------- Import-from-another-AI panel -------
  if (importMode) {
    const promptText = isStaffer ? STAFFER_IMPORT_PROMPT : isCommittee ? COMMITTEE_IMPORT_PROMPT : CANDIDATE_IMPORT_PROMPT;
    const copyPrompt = async () => {
      try {
        await navigator.clipboard.writeText(promptText);
        setPromptCopied(true);
        setTimeout(() => setPromptCopied(false), 2000);
      } catch (clipErr) {
        // Clipboard is denied in some browsers / non-secure contexts;
        // fall back to a manual-select prompt so the user isn't blocked.
        console.warn("[CampaignIntake] clipboard copy failed", clipErr);
        toast.error("Copy failed — select the prompt text and copy manually.");
      }
    };

    const runImport = async () => {
      if (pasteText.trim().length < 10) {
        setImportError("Paste the response from your other AI before importing.");
        return;
      }
      setImportError("");
      setImporting(true);
      try {
        const { data } = await api.post("/campaign/import-extract", {
          source_text: pasteText,
          account_type: form.account_type,
        });
        const issues = Array.isArray(data.top_issues) ? data.top_issues.slice(0, 3) : [];
        while (issues.length < 3) issues.push("");
        setForm({ ...emptyShared, ...data, top_issues: issues });
        setPickedType(true);
        setImportMode(false);
        setStep(1);
      } catch (err) {
        setImportError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setImporting(false);
      }
    };

    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Import from another AI</p>
          <span
            data-testid="import-recommended-badge"
            className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] bg-flag-red text-white px-2 py-0.5 rounded-full"
          >
            Highly recommended
          </span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">
          Skip the typing
        </h1>
        <p className="text-slate-600 mt-2 leading-relaxed">
          If you&rsquo;ve been working with ChatGPT, Gemini, or another AI on your {isCommittee ? "committee" : "campaign"}, bringing that over is the single highest-leverage thing you can do here. It preserves the context you&rsquo;ve built up and tells Electable what it needs to know about your {isCommittee ? "committee, your county, and your slate" : "race, your voice, and your district"} — so every post, speech, and graphic from here on out is grounded in your work. Paste the prompt below into your other AI, then paste its response back here. You&rsquo;ll review every field before anything saves.
        </p>

        {/* Account-type toggle — picks which prompt to display and how to map the result */}
        <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="import-account-type-toggle">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">I'm importing as</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setField("account_type", "candidate")}
              data-testid="import-type-candidate"
              className={`text-left border-2 rounded-lg px-4 py-3 transition-colors ${form.account_type === "candidate" ? "border-flag-red bg-flag-red/5" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center gap-2 font-semibold text-flag-navy">
                <User className="h-4 w-4" /> Candidate
              </div>
              <p className="text-xs text-slate-600 mt-1">Single-race briefing about you, your race, and your opponent.</p>
            </button>
            <button
              type="button"
              onClick={() => setField("account_type", "committee")}
              data-testid="import-type-committee"
              className={`text-left border-2 rounded-lg px-4 py-3 transition-colors ${form.account_type === "committee" ? "border-flag-navy bg-flag-navy/5" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center gap-2 font-semibold text-flag-navy">
                <Users className="h-4 w-4" /> Party Committee
              </div>
              <p className="text-xs text-slate-600 mt-1">County-wide briefing about your committee, slate, and political landscape.</p>
            </button>
            <button
              type="button"
              onClick={() => setField("account_type", "staffer")}
              data-testid="import-type-staffer"
              className={`text-left border-2 rounded-lg px-4 py-3 transition-colors ${form.account_type === "staffer" ? "border-flag-red bg-flag-red/5" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center gap-2 font-semibold text-flag-navy">
                <Briefcase className="h-4 w-4" /> Campaign Staff
              </div>
              <p className="text-xs text-slate-600 mt-1">Staff briefing about the candidate you work for, the operation, and your role.</p>
            </button>
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5">
          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label className="text-sm font-semibold text-slate-800">Step 1 — Copy this prompt into your other AI</Label>
              <Button
                type="button"
                variant="outline"
                onClick={copyPrompt}
                data-testid="import-copy-prompt-btn"
                className="h-9 px-3 border-2 font-semibold"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" /> {promptCopied ? "Copied!" : "Copy prompt"}
              </Button>
            </div>
            <pre
              data-testid="import-prompt-block"
              className="mt-2 max-h-72 overflow-auto bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs sm:text-sm whitespace-pre-wrap font-mono text-slate-800 leading-relaxed"
            >
{promptText}
            </pre>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">Step 2 — Paste the AI's response back here</Label>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={10}
              placeholder="Paste the labeled-list response from ChatGPT / Gemini / etc."
              className="mt-2 text-base font-mono"
              data-testid="import-paste-input"
            />
          </div>

          {importError && (
            <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="import-error">
              {importError}
            </div>
          )}

          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm">
            <span className="font-semibold">You'll review every field.</span> Imported data fills the intake form below — nothing is saved until you confirm on the final step.
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setImportMode(false); setPasteText(""); setImportError(""); }}
              data-testid="import-back-btn"
              className="font-semibold"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            <Button
              type="button"
              onClick={runImport}
              disabled={importing || pasteText.trim().length < 10}
              data-testid="import-submit-btn"
              className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            >
              <ClipboardPaste className="h-4 w-4 mr-1.5" />
              {importing ? "Importing…" : "Import & review"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Account type picker (only shown when no campaign exists yet)
  if (!pickedType) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Get started</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">
          What kind of account is this?
        </h1>
        <p className="text-slate-600 mt-2">
          We tailor the intake and the writing voice based on your answer. Same tools either way — just a different setup.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => {
              setForm((f) => ({ ...f, account_type: "candidate" }));
              setPickedType(true);
              setStep(1);
            }}
            data-testid="intake-account-type-candidate"
            className="text-left bg-white border-2 border-slate-200 hover:border-flag-red rounded-xl p-6 sm:p-8 shadow-sm transition-colors group"
          >
            <div className="h-12 w-12 rounded-lg bg-flag-red/10 text-flag-red flex items-center justify-center">
              <User className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-flag-navy">Candidate</h2>
            <p className="mt-2 text-slate-600 leading-relaxed">
              You're running for an office — school board, council, legislature, state house. Posts are written in your first-person voice.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-flag-red font-semibold">
              I'm a candidate <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </button>

          <button
            type="button"
            onClick={() => {
              setForm((f) => ({ ...f, account_type: "committee" }));
              setPickedType(true);
              setStep(1);
            }}
            data-testid="intake-account-type-committee"
            className="text-left bg-white border-2 border-slate-200 hover:border-flag-navy rounded-xl p-6 sm:p-8 shadow-sm transition-colors group"
          >
            <div className="h-12 w-12 rounded-lg bg-flag-navy/10 text-flag-navy flex items-center justify-center">
              <Users className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-flag-navy">Party Committee</h2>
            <p className="mt-2 text-slate-600 leading-relaxed">
              You speak for a county or area committee — Lincoln Day dinners, GOTV, fundraisers. Posts are written in the committee's institutional “we” voice.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-flag-navy font-semibold">
              We're a committee <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </button>
        </div>

        <div className="mt-6 bg-white border-2 border-flag-red rounded-xl p-5 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="intake-import-card">
          <div className="flex items-start gap-3 min-w-0">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-flag-red text-white flex-shrink-0">
              <Wand2 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-display font-bold text-flag-navy">Already set this up with another AI?</div>
                <span
                  data-testid="intake-import-recommended-badge"
                  className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] bg-flag-red text-white px-2 py-0.5 rounded-full"
                >
                  Highly recommended
                </span>
              </div>
              <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                Bring over the work you&rsquo;ve already done in ChatGPT, Gemini, or another AI. We&rsquo;ll give you a candidate-style or committee-style briefing prompt based on what you pick below, then map the result into your profile — so every post, speech, and graphic from here on out sounds like <em>you</em>, in <em>your</em> race or county.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setImportMode(true)}
            data-testid="intake-import-btn"
            className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            <ClipboardPaste className="h-4 w-4 mr-1.5" /> Import from another AI
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">
            {isCommittee ? "Committee Intake" : "Campaign Intake"}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">
            {isCommittee ? "Set up your committee" : "Set up your campaign"}
          </h1>
          <p className="text-slate-600 mt-2">
            This profile feeds every social post you generate. You can edit it any time.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full" data-testid="intake-account-badge">
            {isCommittee ? <Users className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
            {isCommittee ? "Party Committee" : "Candidate"}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setImportMode(true)}
          data-testid="intake-open-import-btn"
          className="h-10 px-3 border-2 border-flag-red text-flag-red hover:bg-flag-red/5 font-bold"
        >
          <ClipboardPaste className="h-4 w-4 mr-1.5" /> Import from another AI
        </Button>
      </div>

      {/* Stepper */}
      <ol className="mt-8 flex items-center gap-2 sm:gap-3" data-testid="intake-stepper">
        {STEPS.map((s, i) => {
          const state = step === s.id ? "active" : step > s.id ? "done" : "pending";
          return (
            <li key={s.id} className="flex-1 flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setStep(s.id)}
                data-testid={`step-${s.id}-btn`}
                className={`h-9 w-9 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  state === "active"
                    ? "bg-flag-navy text-white"
                    : state === "done"
                    ? "bg-green-700 text-white"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {state === "done" ? <Check className="h-4 w-4" /> : s.id}
              </button>
              <div className="hidden sm:block min-w-0">
                <div className={`text-xs font-bold uppercase tracking-wider ${state === "pending" ? "text-slate-400" : "text-flag-navy"}`}>Step {s.id}</div>
                <div className="text-sm font-semibold text-slate-700 truncate">{s.title}</div>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </li>
          );
        })}
      </ol>

      <form onSubmit={save} className="mt-8 bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-flag-navy">{STEPS[step - 1].title}</h2>
          <p className="text-slate-600 mt-1">{STEPS[step - 1].desc}</p>
        </div>

        {/* Wrap every step's body in a boundary — a crash inside one step
            (like the July 2026 blank-page bug on step 5) now shows a
            readable retry card instead of an empty white page. */}
        <IntakeStepBoundary stepKey={step}>
        {/* ========== STEP 1 ========== */}
        {step === 1 && !isCommittee && !isStaffer && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Candidate name *</Label>
              <Input value={form.candidate_name} onChange={(e) => setField("candidate_name", e.target.value)} className="mt-1 h-12 text-base" placeholder="Jane Candidate" data-testid="intake-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Office sought *</Label>
              <Input value={form.office_sought} onChange={(e) => setField("office_sought", e.target.value)} className="mt-1 h-12 text-base" placeholder="County Legislator" data-testid="intake-office-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">District / jurisdiction</Label>
              <Input value={form.district} onChange={(e) => setField("district", e.target.value)} className="mt-1 h-12 text-base" placeholder="Ontario County Legislature, District 3" data-testid="intake-district-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Election date</Label>
              <Input type="date" value={form.election_date} onChange={(e) => setField("election_date", e.target.value)} className="mt-1 h-12 text-base" data-testid="intake-date-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Your background / bio</Label>
              <Textarea value={form.bio} onChange={(e) => setField("bio", e.target.value)} rows={5} className="mt-1 text-base" placeholder="Born and raised in… Worked as… Lives in… Family… Why I'm running…" data-testid="intake-bio-input" />
            </div>
          </div>
        )}
        {step === 1 && isStaffer && (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Your name *</Label>
                <Input value={form.staffer_name} onChange={(e) => setField("staffer_name", e.target.value)} className="mt-1 h-12 text-base" placeholder="Alex Operations" data-testid="intake-staffer-name-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800">Your staff role *</Label>
                <Input value={form.staffer_role} onChange={(e) => setField("staffer_role", e.target.value)} className="mt-1 h-12 text-base" placeholder="Campaign manager / field director / comms director / volunteer coordinator…" data-testid="intake-staffer-role-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Candidate you work for *</Label>
                <Input value={form.candidate_name} onChange={(e) => setField("candidate_name", e.target.value)} className="mt-1 h-12 text-base" placeholder="Jane Candidate" data-testid="intake-name-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800">Party</Label>
                <Input value={form.party} onChange={(e) => setField("party", e.target.value)} className="mt-1 h-12 text-base" placeholder="Republican" data-testid="intake-party-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Office sought *</Label>
                <Input value={form.office_sought} onChange={(e) => setField("office_sought", e.target.value)} className="mt-1 h-12 text-base" placeholder="County Legislator" data-testid="intake-office-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800">District / jurisdiction</Label>
                <Input value={form.district} onChange={(e) => setField("district", e.target.value)} className="mt-1 h-12 text-base" placeholder="Ontario County Legislature, District 3" data-testid="intake-district-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Election date</Label>
                <Input type="date" value={form.election_date} onChange={(e) => setField("election_date", e.target.value)} className="mt-1 h-12 text-base" data-testid="intake-date-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800">Primary date (if any)</Label>
                <Input type="date" value={form.primary_date} onChange={(e) => setField("primary_date", e.target.value)} className="mt-1 h-12 text-base" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Your responsibilities</Label>
              <Textarea value={form.staffer_responsibilities} onChange={(e) => setField("staffer_responsibilities", e.target.value)} rows={3} className="mt-1 text-base" placeholder="What you are specifically responsible for — volunteers, field, comms, scheduling, etc." data-testid="intake-staffer-responsibilities" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">What you most need help with</Label>
              <Textarea value={form.staffer_help_needed} onChange={(e) => setField("staffer_help_needed", e.target.value)} rows={3} className="mt-1 text-base" placeholder="Volunteer recruitment? GOTV planning? Press? Be specific — the Advisor will tailor advice." data-testid="intake-staffer-help" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Candidate bio (optional)</Label>
              <Textarea value={form.bio} onChange={(e) => setField("bio", e.target.value)} rows={4} className="mt-1 text-base" placeholder="Background on the candidate you work for — informs surrogate speeches and content." data-testid="intake-bio-input" />
            </div>
          </div>
        )}
        {step === 1 && isCommittee && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Committee name *</Label>
              <Input value={form.committee_name} onChange={(e) => setField("committee_name", e.target.value)} className="mt-1 h-12 text-base" placeholder="Ontario County Republican Committee" data-testid="intake-committee-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Party *</Label>
              <Input value={form.party} onChange={(e) => setField("party", e.target.value)} className="mt-1 h-12 text-base" placeholder="Republican" data-testid="intake-party-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">County / jurisdiction *</Label>
              <Input value={form.jurisdiction} onChange={(e) => setField("jurisdiction", e.target.value)} className="mt-1 h-12 text-base" placeholder="Ontario County, NY" data-testid="intake-jurisdiction-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Committee background (optional)</Label>
              <Textarea value={form.bio} onChange={(e) => setField("bio", e.target.value)} rows={4} className="mt-1 text-base" placeholder="Founded… Enrolled members… Recent wins… What we stand for locally…" data-testid="intake-bio-input" />
            </div>
          </div>
        )}

        {/* ========== STEP 2 ========== */}
        {step === 2 && !isCommittee && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Top 3 issues in this race</Label>
              <p className="text-sm text-slate-500 mt-1">Your priorities. Keep each one short and concrete.</p>
              <div className="mt-3 space-y-3">
                {form.top_issues.map((issue, i) => (
                  <Input
                    key={`issue-slot-${i}`}
                    value={issue}
                    onChange={(e) => setIssue(i, e.target.value)}
                    placeholder={`Issue ${i + 1} (e.g., "Stop the tax hikes")`}
                    className="h-12 text-base"
                    data-testid={`intake-issue-${i + 1}-input`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {step === 2 && isCommittee && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Top countywide issues &amp; priorities</Label>
              <p className="text-sm text-slate-500 mt-1">The themes the committee leads on across the county. Keep each one short.</p>
              <div className="mt-3 space-y-3">
                {form.top_issues.map((issue, i) => (
                  <Input
                    key={`priority-slot-${i}`}
                    value={issue}
                    onChange={(e) => setIssue(i, e.target.value)}
                    placeholder={`Priority ${i + 1} (e.g., "Lower county taxes")`}
                    className="h-12 text-base"
                    data-testid={`intake-issue-${i + 1}-input`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Key recurring events the committee runs</Label>
              <p className="text-sm text-slate-500 mt-1">List them by name — drafts will reference these by name when relevant.</p>
              <Textarea value={form.key_events} onChange={(e) => setField("key_events", e.target.value)} rows={5} className="mt-1 text-base" placeholder={"e.g.\n- Lincoln Day Dinner (annual, March)\n- Fall fundraiser at Wilson Lodge\n- Monthly meet-and-greets at local diners\n- GOTV phone bank weekends"} data-testid="intake-key-events-input" />
            </div>
          </div>
        )}

        {/* ========== STEP 3 ========== */}
        {step === 3 && !isCommittee && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Opponent name</Label>
              <Input value={form.opponent_name} onChange={(e) => setField("opponent_name", e.target.value)} className="mt-1 h-12 text-base" placeholder="John Incumbent" data-testid="intake-opponent-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Opponent's platform / top issues</Label>
              <Textarea value={form.opponent_platform} onChange={(e) => setField("opponent_platform", e.target.value)} rows={4} className="mt-1 text-base" placeholder="What is your opponent running on?" data-testid="intake-opponent-platform-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Opponent's vulnerabilities / negatives</Label>
              <Textarea value={form.opponent_vulnerabilities} onChange={(e) => setField("opponent_vulnerabilities", e.target.value)} rows={4} className="mt-1 text-base" placeholder="Record, voting history, missed meetings, broken promises, scandals — whatever's on the record." data-testid="intake-opponent-vuln-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Your own weaknesses (so we avoid them)</Label>
              <Textarea value={form.own_weaknesses} onChange={(e) => setField("own_weaknesses", e.target.value)} rows={3} className="mt-1 text-base" placeholder="What might your opponent attack you on? We'll steer drafts around it." data-testid="intake-own-weaknesses-input" />
            </div>
          </div>
        )}
        {step === 3 && isCommittee && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Opposing party's countywide messaging</Label>
              <p className="text-sm text-slate-500 mt-1">What is the other side running on across the county? We'll contrast against the message, not the people.</p>
              <Textarea value={form.opposition_messaging} onChange={(e) => setField("opposition_messaging", e.target.value)} rows={5} className="mt-1 text-base" placeholder="Their top talking points, the spending they're defending, the records you'd contrast against." data-testid="intake-opposition-messaging-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">Vulnerabilities to avoid</Label>
              <Textarea value={form.own_weaknesses} onChange={(e) => setField("own_weaknesses", e.target.value)} rows={3} className="mt-1 text-base" placeholder="What might the other side attack the committee on? We'll steer drafts around it." data-testid="intake-own-weaknesses-input" />
            </div>
          </div>
        )}

        {/* ========== STEP 4 ========== */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Voice / tone</Label>
              <p className="text-sm text-slate-500 mt-1">
                {isCommittee
                  ? "Pick how the committee sounds publicly. You can change it any time."
                  : "Pick the voice that sounds most like you. You can change it any time."}
              </p>
              <div className="mt-3" data-testid="intake-tone-select-wrapper">
                <Select value={form.tone} onValueChange={(v) => setField("tone", v)}>
                  <SelectTrigger className="h-12 text-base" data-testid="intake-tone-trigger">
                    <SelectValue placeholder="Choose a voice" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`intake-tone-option-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* ========== STEP 5 — State of the Race (optional, both account types) ========== */}
        {step === 5 && (() => {
          // Bug (Jul 2026): step 5 rendered blank for every signup because
          // this line was assigning the RAW field array (`SOTR_FIELDS` /
          // `SOTR_FIELDS_COMMITTEE`) instead of the pre-grouped shape.
          // `sotr.groups.map(...)` on a raw array → `.groups` is undefined
          // → TypeError, React unmounts, blank page. The `_GROUPED`
          // constants produce `{ groups, byGroup }`, which is what the
          // JSX below assumes.
          const sotr = isCommittee ? SOTR_GROUPED_COMMITTEE : SOTR_GROUPED;
          const groups = sotr?.groups || [];
          return (
          <div className="space-y-6" data-testid="intake-sotr-form">
            <div className="rounded-lg border border-flag-red/30 bg-flag-red/5 px-4 py-3 text-sm text-slate-700">
              <strong className="font-semibold text-flag-navy">Every field on this page is optional.</strong> Fill in as much as you have — even one or two of these makes the Advisor and the post / speech / graphic generators sharper. You can come back and add the rest in Settings.
            </div>
            {groups.map((group) => (
              <div key={group} className="space-y-4" data-testid={`intake-sotr-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-display text-lg font-bold text-flag-navy">{group}</h3>
                {(sotr.byGroup?.get(group) || []).map(([, key, label, helper, multiline]) => (
                  <div key={key}>
                    <Label htmlFor={`sotr-${key}`} className="text-sm font-semibold text-slate-800">{label}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">{helper}</p>
                    {multiline ? (
                      <Textarea
                        id={`sotr-${key}`}
                        value={form[key] || ""}
                        onChange={(e) => setField(key, e.target.value)}
                        rows={3}
                        className="mt-2 text-base"
                        data-testid={`intake-sotr-${key}`}
                      />
                    ) : (
                      <Input
                        id={`sotr-${key}`}
                        value={form[key] || ""}
                        onChange={(e) => setField(key, e.target.value)}
                        className="mt-2 h-11 text-base"
                        data-testid={`intake-sotr-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          );
        })()}
        </IntakeStepBoundary>

        {error && (
          <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="intake-error">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="h-11 px-4 border-2"
            data-testid="intake-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {step < STEPS.length ? (
            <Button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="h-11 px-5 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
              data-testid="intake-next-btn"
            >
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={saving}
              className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
              data-testid="intake-save-btn"
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : isCommittee ? "Save committee profile" : "Save campaign profile"}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
